"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { customerApi, CustomerApiError } from "@/lib/customer-api";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";

/**
 * Heart toggle rendered next to the category/destination H1. Adds or
 * removes the current slug from the logged-in customer's destination
 * favorites. Anonymous click bounces to /account/login.
 */
export default function DestinationFavoriteButton({ slug, displayName }) {
  // INTL bug-batch 2026-06-10 item 2: all five strings below were
  // hardcoded NL ("Bewaar bestemming" leaked on .de/.fr/.co.uk).
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const router = useRouter();
  const pathname = usePathname();
  const [hydrated, setHydrated] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [favorite, setFavorite] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const controller = new AbortController();
    const slugLower = String(slug).toLowerCase();
    (async () => {
      try {
        // meQuiet (E2E QA 2026-06-10): no network call for anonymous
        // visitors (hint-gated), shared single-flight when authed.
        const profile = await customerApi.meQuiet();
        if (!profile) return; // anonymous
        if (controller.signal.aborted) return;
        const fav = await customerApi.favorites({ signal: controller.signal });
        if (controller.signal.aborted) return;
        const match = fav.destinations.find((d) => d.slug === slugLower);
        setAuthed(true);
        setFavorite(match ? { id: match.id } : null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.name === "AbortError") return;
        if (err instanceof CustomerApiError && err.status === 401) {
          setAuthed(false);
        }
      } finally {
        if (!controller.signal.aborted) setHydrated(true);
      }
    })();
    return () => controller.abort();
  }, [slug]);

  function bounceToLogin() {
    const returnTo = encodeURIComponent(pathname || "/");
    router.push(`/account/login?returnTo=${returnTo}`);
  }

  async function onToggle() {
    if (pending) return;
    if (!authed) return bounceToLogin();
    setPending(true);
    try {
      if (favorite) {
        await customerApi.deleteFavorite(favorite.id);
        setFavorite(null);
      } else {
        const created = await customerApi.createFavorite({
          kind: "destination",
          slug: String(slug).toLowerCase(),
          displayName: displayName || String(slug),
        });
        setFavorite({ id: created.id });
      }
    } catch {
      // Soft fail.
    } finally {
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!hydrated || pending}
      aria-pressed={!!favorite}
      aria-label={
        favorite
          ? t("favorites.remove_aria", { name: displayName || slug })
          : t("favorites.add_aria", { name: displayName || slug })
      }
      title={
        favorite
          ? t("favorites.title_saved")
          : t("favorites.title_save")
      }
      className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
        favorite
          ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
          : "border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50"
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill={favorite ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4"
        aria-hidden="true"
      >
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
      <span>{favorite ? t("favorites.destination_saved") : t("favorites.save_destination")}</span>
    </button>
  );
}
